import { debounce } from "lodash";
import React, { useEffect, useMemo, useState } from 'react';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';

const AsyncAutoComplete = ({
                               label,
                               placeholder,
                               value,
                               multiple,
                               handleOnChange,
                               handleOnError = () => {
                               },
                               fetch,
                               required = false,
                               unknownOption = false,
                               anyOption = false,
                               variant = "outlined",
                               noOptionsText = "No options",
                               minQueryLength = 0,
                               ...props
                           }) => {
    const [inputValue, setInputValue] = useState('');
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState(
        multiple ? (value || []) : (value || null)
    );

    const error = required && (
        multiple
            ? !value || value.length === 0
            : value?.id === null || value?.id === undefined
    );

    useEffect(() => {
        if (multiple) {
            setSelectedOptions(value || []);
        } else {
            setSelectedOptions(value || null);
        }
        if (required) {
            handleOnError(error);
        }
    }, [value]);

    const changeSelectedOption = (options) => {
        setSelectedOptions(options);
        handleOnChange(options);
    };

    const populate = (query) => {
        let active = true;

        (async () => {
          const response = await fetch(query);
          
            const {
                data,
                status: responseStatus
            } = response.data;
            if (active) {
                if (unknownOption) {
                    setOptions([{ id: null, name: 'Unknown' }].concat(...data));
                } else if (anyOption) {
                    setOptions([{ id: 'any', name: `Any ${label}` }].concat(...data));
                } else {
                    setOptions(data);
                }
            }
            setLoading(false);
        })();

        return () => {
            active = false;
        };
    };

    const debounceLoadData = useMemo(() => debounce(populate, 700), [fetch]);

    const queryTooShort = inputValue.trim().length < minQueryLength;

    useEffect(() => {
        if (!open) return;
        if (queryTooShort) {
            debounceLoadData.cancel();
            setOptions([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        debounceLoadData(inputValue);
    }, [inputValue, open]);

    return (
        <Autocomplete
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            noOptionsText={queryTooShort ? `Type at least ${minQueryLength} characters` : noOptionsText}
            getOptionSelected={(option, value) => option?.id === value?.id}
            getOptionLabel={(option) => {
                if (!option || typeof option !== "object") {
                    return "";
                }
                return option.name ?? "";
            }}
            options={options}
            loading={loading}
            value={selectedOptions}
            multiple={multiple}
            onChange={(event, value) => {
                if (multiple) {
                    changeSelectedOption(value || []);
                } else {
                    changeSelectedOption(value || null);
                }
            }}
            onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    variant={variant}
                    inputProps={{ ...params.inputProps, placeholder: placeholder }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                    error={error}
                    required={required}
                />
            )}
            {...props}
        />
    );
}

export default AsyncAutoComplete;
